import * as amplitude from '@amplitude/analytics-browser';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { useId } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { match, Pattern } from 'ts-pattern';

import { useCheckEmailAddressInUse, useCreateUncompletedRegistration } from '~/api/auth';
import { Alert, Button, ErrorMessage, ExternalLink, Redirect } from '~/components';
import { Field, FieldGroup, Form, Input, Select } from '~/components/Form';
import { AmplitudeEventName } from '~/constants/amplitude';
import { countryCallingCodes } from '~/constants/countries';
import { EMAIL } from '~/constants/regex';
import { useIntl } from '~/hooks/useIntl';
import { routes } from '~/providers/RouterProvider/router.routes';
import { parsePhoneNumber } from '~/utils/libphonenumberjs';
import { isNotBlank } from '~/utils/string';

import { AuthLayoutContent } from '../AuthLayout/AuthLayoutContent';
import { AuthLayoutTitleGroup } from '../AuthLayout/AuthLayoutTitleGroup';
import { PasswordField } from '../PasswordField/PasswordField';
import { PreviousStepButton } from './PreviousStepButton';
import { useRegistrationContext } from './Registration.context';
import {
  buttonsStyle,
  buttonStyle,
  callingCodeItemStyle,
  callingCodeListStyle,
  contentWrapperStyle,
  formStyle,
} from './RegistrationAccountInformation.css';
import { RegistrationAddReferralCodeButton } from './RegistrationReferralCode/RegistrationAddReferralCodeButton';
import { RegistrationReferralCode } from './RegistrationReferralCode/RegistrationReferralCode';
import { checkVatInformationStepCompleted } from './utils';

const CALLING_CODE_OPTIONS = [
  { callingCode: countryCallingCodes.BE, countryCode: 'BE', flag: '🇧🇪' },
  { callingCode: countryCallingCodes.NL, countryCode: 'NL', flag: '🇳🇱' },
  { callingCode: '', countryCode: null, flag: '🏳️' },
] as const;

export const RegistrationAccountInformation = () => {
  const createUncompletedRegistrationMutation = useCreateUncompletedRegistration();
  const checkEmailAddressInUseMutation = useCheckEmailAddressInUse();

  const { accountInformation, hadReferralCode, setAccountInformation, vatInformation } = useRegistrationContext();

  const { control, formState, handleSubmit, watch } = useForm({
    defaultValues: accountInformation,
  });
  const formId = useId();
  const watchCallingCode = watch('callingCode');

  const [autoAnimateRef] = useAutoAnimate();
  const navigate = useNavigate();
  const { displayNameOfCountry } = useIntl();
  const { t } = useTranslation(['auth', 'validation']);

  if (!checkVatInformationStepCompleted(vatInformation)) return <Redirect to={routes.register} />;

  const onSubmit = handleSubmit((data) => {
    setAccountInformation(data);

    createUncompletedRegistrationMutation.mutate(
      {
        firstName: vatInformation.founder?.firstName ?? '',
        lastName: vatInformation.founder?.lastName ?? vatInformation.companyName ?? '',
        emailAddress: data.emailAddress,
        phoneNumber: match(data.callingCode)
          .with(
            '32',
            () => parsePhoneNumber(`+${data.callingCode}${data.phoneNumber}`)?.format('E.164') ?? `+${data.callingCode}${data.phoneNumber}`,
          )
          .with('31', () => `+${data.callingCode}${data.phoneNumber}`)
          .with(Pattern.any, () => data.phoneNumber)
          .exhaustive(),
      },
      {
        onSettled: () => {
          amplitude.setUserId(data.emailAddress);
          amplitude.track(AmplitudeEventName.RegistrationStep2Completed);

          navigate(routes.registerBusinessInformation);
        },
      },
    );
  });

  return (
    <AuthLayoutContent>
      <PreviousStepButton to="vatNumber" />

      <AuthLayoutTitleGroup
        eyebrow={t('auth:registrationPage.common.header.pill')}
        subtitle={t('auth:registrationPage.stepAccountInformation.header.subtitle')}
        title={t('auth:registrationPage.stepAccountInformation.header.title', {
          name: vatInformation.founder?.firstName ?? vatInformation.companyName ?? '',
        })}
      />

      <div className={contentWrapperStyle} ref={autoAnimateRef}>
        {!vatInformation.hasEligibleNacebelCodes && (
          <Alert
            announce
            icon="Warning"
            status="warning"
            title={t('auth:registrationPage.stepAccountInformation.alertNoElibibleNacebelCodes.title')}
          >
            {t('auth:registrationPage.stepAccountInformation.alertNoElibibleNacebelCodes.description')}
          </Alert>
        )}

        <Form className={formStyle} id={formId} onSubmit={onSubmit}>
          <Controller
            control={control}
            name="emailAddress"
            render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
              <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
                <Field.Label>{t('auth:registrationPage.stepAccountInformation.form.emailField.label')}</Field.Label>

                <Input
                  autoFocus={!accountInformation.emailAddress}
                  onBlur={onBlur}
                  onValueChange={(value) => {
                    onChange(value);
                    if (checkEmailAddressInUseMutation.isError) checkEmailAddressInUseMutation.reset();
                  }}
                  ref={ref}
                  type="email"
                  value={value}
                />

                <Field.Error match={!!error}>{error?.message}</Field.Error>
                {checkEmailAddressInUseMutation.isError && (
                  <ErrorMessage>
                    <Trans
                      components={{ email: <ExternalLink /> }}
                      i18nKey="auth:registrationPage.stepAccountInformation.form.emailField.errors.generic"
                    />
                  </ErrorMessage>
                )}
              </Field.Root>
            )}
            rules={{
              required: { value: true, message: t('validation:required') },
              maxLength: {
                value: 255,
                message: t('validation:maxLength', {
                  attribute: t('auth:registrationPage.stepAccountInformation.form.emailField.label'),
                  max: 255,
                }),
              },
              pattern: { value: EMAIL, message: t('validation:email.invalid') },
              validate: {
                notBlank: (value) => isNotBlank(value) || t('validation:required'),
                unique: async (value) => {
                  try {
                    const inUse = await checkEmailAddressInUseMutation.mutateAsync({ emailAddress: value.trim() });

                    return !inUse || t('auth:registrationPage.stepAccountInformation.form.emailField.errors.exists');
                  } catch {
                    // Block submission. The detailed reason is shown by the `ErrorMessage` above.
                    return false;
                  }
                },
              },
            }}
          />

          <PasswordField control={control} />

          <FieldGroup.Root>
            <FieldGroup.Legend>{t('auth:registrationPage.stepAccountInformation.form.phoneNumberField.label')}</FieldGroup.Legend>

            <FieldGroup.Items>
              <Controller
                control={control}
                name="callingCode"
                render={({ field: { name, onChange, value }, fieldState: { isDirty, isTouched } }) => (
                  // The calling code has no validation of its own; it shares the phone number's invalid state so that
                  // both halves of the field group are marked at once.
                  <Field.Root dirty={isDirty} invalid={!!formState.errors.phoneNumber} name={name} touched={isTouched}>
                    <Field.Label srOnly>{t('auth:registrationPage.stepAccountInformation.form.callingCodeField.label')}</Field.Label>

                    <Select.Root
                      items={CALLING_CODE_OPTIONS.map(({ callingCode, flag }) => ({ value: callingCode, label: flag }))}
                      onValueChange={onChange}
                      value={value}
                    >
                      <Select.Button aria-label={t('auth:registrationPage.stepAccountInformation.form.callingCodeField.label')} />

                      <Select.List align="start" className={callingCodeListStyle}>
                        {CALLING_CODE_OPTIONS.map(({ callingCode, countryCode, flag }) => (
                          <Select.Item className={callingCodeItemStyle} key={callingCode} value={callingCode}>
                            {flag}{' '}
                            {countryCode
                              ? `${displayNameOfCountry(countryCode)} +${callingCode}`
                              : t('auth:registrationPage.stepAccountInformation.form.callingCodeField.options.other')}
                          </Select.Item>
                        ))}
                      </Select.List>
                    </Select.Root>
                  </Field.Root>
                )}
              />

              <Controller
                control={control}
                name="phoneNumber"
                render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
                  <Field.Root dirty={isDirty} grow invalid={invalid} name={name} touched={isTouched}>
                    <Field.Label srOnly>{t('auth:registrationPage.stepAccountInformation.form.phoneNumberField.label')}</Field.Label>

                    <Input
                      onBlur={onBlur}
                      onValueChange={onChange}
                      prefix={watchCallingCode !== '' ? `+${watchCallingCode}` : undefined}
                      ref={ref}
                      type="tel"
                      value={value}
                    />
                    <Field.Error match={!!error}>{error?.message}</Field.Error>
                  </Field.Root>
                )}
                rules={{
                  required: { value: true, message: t('validation:required') },
                  validate: {
                    notBlank: (value) => isNotBlank(value) || t('validation:required'),
                    format: (value, { callingCode }) =>
                      callingCode !== '32' ||
                      !!parsePhoneNumber(`+${callingCode}${value}`)?.isValid() ||
                      t('validation:phoneNumber.format'),
                  },
                }}
              />
            </FieldGroup.Items>
          </FieldGroup.Root>
        </Form>

        <RegistrationReferralCode />

        <div className={buttonsStyle}>
          {hadReferralCode && <RegistrationAddReferralCodeButton className={buttonStyle} />}

          <Button
            className={buttonStyle}
            form={formId}
            loading={formState.isValidating || createUncompletedRegistrationMutation.isPending}
            type="submit"
          >
            {t('auth:registrationPage.stepAccountInformation.form.submitButton')}
          </Button>
        </div>
      </div>
    </AuthLayoutContent>
  );
};
